import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import {
    BundledProductsData,
    CardDetailDtoLimited,
    VoucherAmountUnifiedDetail,
    VoucherProductUnifiedDetail,
    VoucherType,
} from 'store/api-slice/types'
import {
    CardPersonDto,
    GetCartItemPresenter,
    ProductVoucherPresenter,
} from 'store/api-slice/hooks'
import {
    PRODUCT_TYPE_BUNDLE,
    PRODUCT_TYPE_VOUCHER,
    VOUCHER_TYPE_AMOUNT,
    VOUCHER_TYPE_PRODUCT,
} from 'constants/app'
import {
    checkStateVouchersCoherence,
    mapCartVouchersData,
} from 'helpers/vouchers'

export type VoucherBoxField = 'amount' | 'voucherSignature'

export interface ValueVoucherBox extends VoucherAmountUnifiedDetail {
    cartItemId?: string
    productId?: string
    personDetail?: never
    cardDetail?: never
    bundleData?: never
    unitPrice?: never
}

export interface CardVoucherBox extends VoucherProductUnifiedDetail {
    cartItemId?: string
    productId?: string
    personDetail?: CardPersonDto
    cardDetail?: CardDetailDtoLimited
    bundleData?: BundledProductsData
    unitPrice?: number
    amount?: never
}

export type VoucherBox = ValueVoucherBox | CardVoucherBox

interface InitialState {
    vouchersType: VoucherType | null
    quantity: number
    areVouchersVisible: boolean
    valueVoucherProductId?: string
    cardVouchersProducts: ProductVoucherPresenter[]
    vouchersBoxes: VoucherBox[]
    hasUnlimitedPlusBeenOffered: boolean
    shippingPrice: number | null
}

const emptyValueVoucher: ValueVoucherBox = {
    voucherType: VOUCHER_TYPE_AMOUNT,
    voucherSignature: [],
    productType: PRODUCT_TYPE_VOUCHER,
    amount: 0,
}

const emptyCardVoucher: CardVoucherBox = {
    voucherType: VOUCHER_TYPE_PRODUCT,
    voucherSignature: [],
    productType: PRODUCT_TYPE_VOUCHER,
}

const initialState: InitialState = {
    vouchersType: null,
    quantity: 1,
    areVouchersVisible: false,
    cardVouchersProducts: [],
    vouchersBoxes: [],
    hasUnlimitedPlusBeenOffered: false,
    shippingPrice: null,
}

const vouchersSlice = createSlice({
    name: 'vouchers',
    initialState,
    reducers: {
        addBlankVoucher(state) {
            state.quantity += 1
            state.vouchersBoxes.push({
                ...(state.vouchersType === VOUCHER_TYPE_AMOUNT
                    ? emptyValueVoucher
                    : emptyCardVoucher),
                cartItemId: uuidv4(),
            })
        },
        restoreVouchersFromCart(
            state,
            action: PayloadAction<{
                cartVouchers: GetCartItemPresenter[]
                personsData: CardPersonDto[]
            }>
        ) {
            const { cartVouchers, personsData } = action.payload
            const restoredVouchers = mapCartVouchersData({
                cartVouchers,
                personsData,
            })

            // cancel restoring if no vouchers or vouchers types are mixed
            if (
                !restoredVouchers.length ||
                !checkStateVouchersCoherence(restoredVouchers)
            )
                return

            // determined by the first voucher - all vouchers should be the same type
            const vouchersType = restoredVouchers[0].voucherType || null

            state.vouchersType = vouchersType
            state.quantity = cartVouchers.length
            state.areVouchersVisible = true
            state.vouchersBoxes = [...restoredVouchers]
        },
        removeLastVoucher(state) {
            state.quantity -= 1
            state.vouchersBoxes.splice(-1)
        },
        removeAllVouchers(state) {
            state = { ...initialState }
        },
        setVoucherAmount(
            state,
            action: PayloadAction<{
                // TODO: change assigning items by index, to for example by cartitemid
                // - fix to do globally on all products
                index: number
                amount: number
            }>
        ) {
            const { index, amount } = action.payload

            state.vouchersBoxes[index].amount = amount
            state.vouchersBoxes[index].productId = state.valueVoucherProductId
        },
        setVoucherPerson(
            state,
            action: PayloadAction<{
                index: number
                personDetail: CardPersonDto
            }>
        ) {
            const { index, personDetail } = action.payload

            state.vouchersBoxes[index].personDetail = { ...personDetail }
            state.vouchersBoxes[index].cardDetail = undefined
            state.vouchersBoxes[index].bundleData = undefined
            state.vouchersBoxes[index].unitPrice = undefined
            state.vouchersBoxes[index].productId = undefined
            state.vouchersBoxes[index].voucherSignature = []
        },
        setVoucherCard(
            state,
            action: PayloadAction<{
                index: number
                productId: string
                cardDetail: CardDetailDtoLimited
                bundleData: BundledProductsData
                unitPrice?: number
            }>
        ) {
            const { index, productId, cardDetail, bundleData, unitPrice } =
                action.payload

            state.vouchersBoxes[index].productId = productId
            state.vouchersBoxes[index].cardDetail = { ...cardDetail }
            state.vouchersBoxes[index].bundleData = [...bundleData]
            state.vouchersBoxes[index].unitPrice = unitPrice
            state.vouchersBoxes[index].voucherSignature = []
        },
        resetVoucherCard(state, action: PayloadAction<number>) {
            const index = action.payload

            state.vouchersBoxes[index].productId = undefined
            state.vouchersBoxes[index].cardDetail = undefined
            state.vouchersBoxes[index].bundleData = undefined
            state.vouchersBoxes[index].unitPrice = undefined
            state.vouchersBoxes[index].voucherSignature = []
        },
        setVoucherSignature(
            state,
            action: PayloadAction<{
                index: number
                signature: string[]
            }>
        ) {
            const { index, signature } = action.payload
            state.vouchersBoxes[index].voucherSignature = signature
        },
        removeVoucher(state, action: PayloadAction<number>) {
            const index = action.payload

            state.quantity -= 1
            state.vouchersBoxes.splice(index, 1)
        },
        setVouchersVisible(state) {
            state.areVouchersVisible = true
        },
        setVouchersProducts(
            state,
            action: PayloadAction<ProductVoucherPresenter[]>
        ) {
            const { productId } =
                action.payload.find(
                    (dataItem) => dataItem.productType === PRODUCT_TYPE_VOUCHER
                ) || {}
            const bundledCardVouchers =
                action.payload.filter(
                    (dataItem) => dataItem.productType === PRODUCT_TYPE_BUNDLE
                ) || {}

            state.valueVoucherProductId = productId
            state.cardVouchersProducts = [...bundledCardVouchers]
        },
        setIsUnlimitedPlusBeenOffered(state, action: PayloadAction<boolean>) {
            state.hasUnlimitedPlusBeenOffered = action.payload
        },
        setShippingPrice(state, action: PayloadAction<number>) {
            state.shippingPrice = action.payload
        },
        setVouchersType(state, action: PayloadAction<VoucherType>) {
            const newVoucherType = action.payload
            state.vouchersType = newVoucherType
            state.quantity = 1
            state.areVouchersVisible = false
            state.vouchersBoxes = [
                {
                    ...(newVoucherType === VOUCHER_TYPE_AMOUNT
                        ? emptyValueVoucher
                        : emptyCardVoucher),
                    cartItemId: uuidv4(),
                },
            ]
        },
    },
})

export const {
    addBlankVoucher,
    restoreVouchersFromCart,
    removeLastVoucher,
    removeVoucher,
    removeAllVouchers,
    setVouchersVisible,
    setVouchersProducts,
    setVoucherAmount,
    setVoucherPerson,
    setVoucherCard,
    resetVoucherCard,
    setVoucherSignature,
    setIsUnlimitedPlusBeenOffered,
    setShippingPrice,
    setVouchersType,
} = vouchersSlice.actions
export default vouchersSlice.reducer
